import React, { useMemo } from 'react';
import {
  BarCustomLayerProps,
  ComputedBarDatum,
} from '@nivo/bar/dist/types/types';
import { BarDatum } from '@nivo/bar';
import { animated } from '@react-spring/web';

const calculateTooltipWidth = (label: string) => {
  // Create a dummy canvas to measure the label's width
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (context) {
    context.font = 'bold 12px Karla'; // Match your desired font
    return context.measureText(label).width;
  }

  return 8 * label.length;
};

export const ValueDifferencePopoverLayer = (
  props: BarCustomLayerProps<BarDatum>,
) => {
  const groupsValues = useMemo(() => {
    const accumulator: {
      [group: string | number]: ComputedBarDatum<BarDatum>[];
    } = {};
    // Group bars by their group index
    const groups = props.bars.reduce((acc, bar) => {
      if (!(bar.data.indexValue in acc)) acc[bar.data.indexValue] = [];
      acc[bar.data.indexValue].push(bar);
      return acc;
    }, accumulator);
    return Object.values(groups);
  }, [props.bars]);

  const totalWidth = props.bars.reduce((acc, bar) => acc + bar.width, 0);
  const totalHeight = props.bars.reduce((acc, bar) => acc + bar.height, 0);
  const shouldDisplayHorizontalBars = totalWidth > totalHeight;

  return (
    <g>
      {groupsValues.map((group, groupIndex) => {
        // Calculate the group's center
        const groupX = Math.min(...group.map((bar) => bar.x));
        const groupWidth =
          Math.max(...group.map((bar) => bar.x + bar.width)) - groupX;
        const groupCenterX = groupX + groupWidth / 2;

        // Position the element above the group
        const groupY = Math.min(...group.map((bar) => bar.y));

        const groupHeight =
          Math.max(...group.map((bar) => bar.y + bar.height)) - groupY;
        const groupCenterY = groupY + groupHeight / 2;

        const values = group.map((bar) =>
          Number.parseFloat(String(bar.data.value)),
        );
        const difference = (Math.max(...values) / Math.min(...values)).toFixed(
          1,
        );

        const rectWidth = calculateTooltipWidth(difference) + 6 * 4;
        const rectHeight = 18;

        const rectX = shouldDisplayHorizontalBars
          ? groupWidth + groupX + 10
          : groupCenterX - rectWidth / 2;
        const rectY = shouldDisplayHorizontalBars
          ? groupCenterY - rectHeight / 2
          : groupY - rectHeight - 10;

        if (!isFinite(Number.parseFloat(difference))) {
          return null;
        }

        return (
          <animated.g key={groupIndex}>
            <rect
              x={rectX}
              y={rectY}
              rx={2}
              ry={2}
              width={rectWidth}
              height={rectHeight}
              fill="#F7F7F7"
            ></rect>
            <text
              x={rectX + rectWidth / 2}
              y={rectY + rectHeight / 2 + 5}
              textAnchor="middle"
              style={{
                fill: 'black',
                fontSize: '12px',
                fontWeight: 'bold',
              }}
            >
              {difference}x
            </text>
          </animated.g>
        );
      })}
    </g>
  );
};
